import type { MessageTree } from '../types';

const en = {
  common: {
    appName: 'BatamX Admin',
    platformConsole: 'Platform Console',
    admin: 'Admin',
    loading: 'Loading…',
    save: 'Save',
    saveChanges: 'Save Changes',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    add: 'Add',
    create: 'Create',
    edit: 'Edit',
    update: 'Update',
    delete: 'Delete',
    deleting: 'Deleting…',
    duplicate: 'Duplicate',
    remove: 'Remove',
    confirm: 'Confirm',
    search: 'Search',
    refresh: 'Refresh',
    retry: 'Retry',
    back: 'Back',
    discardChanges: 'Discard Changes',
    logout: 'Logout',
    view: 'View',
    viewAll: 'View all',
    yes: 'Yes',
    no: 'No',
    optional: 'Optional',
    required: 'Required',
    total: 'Total',
    actions: 'Actions',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    country: 'Country',
    currency: 'Currency',
    code: 'Code',
    description: 'Description',
    position: 'Position',
    sequence: 'Sequence',
    type: 'Type',
    key: 'Key',
    label: 'Label',
    none: 'None',
    unknown: 'Unknown',
    notAvailable: '—',
    noResults: 'No results. Try a different search.',
    noData: 'No data',
    selectPlaceholder: 'Select…',
    requiredFieldError: '{field} is required.',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    loadFailed: 'Failed to load {resource}',
    confirmDelete: 'Delete "{name}"? This cannot be undone.',
    pagination: {
      showing: 'Showing {from}–{to} of {total}',
      rowsPerPage: 'Rows per page',
      page: 'Page {current} of {total}',
      previous: 'Previous',
      next: 'Next',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      hidden: 'Hidden',
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      noShow: 'No Show',
      unpaid: 'Unpaid',
      paid: 'Paid',
      partial: 'Partial',
      overpaid: 'Overpaid',
      void: 'Void',
    },
    roles: {
      owner: 'Owner',
      admin: 'Admin',
      batamx: 'BatamX',
      staff: 'Staff',
      customer: 'Customer',
    },
    paymentMethods: {
      cash: 'Cash',
      credit: 'Credit',
      debit: 'Debit',
      ewallet: 'E-Wallet',
      bankTransfer: 'Bank Transfer',
      voucher: 'Voucher',
      point: 'Point',
      other: 'Other',
    },
    months: {
      '1': 'January',
      '2': 'February',
      '3': 'March',
      '4': 'April',
      '5': 'May',
      '6': 'June',
      '7': 'July',
      '8': 'August',
      '9': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    },
    monthsShort: {
      '1': 'Jan',
      '2': 'Feb',
      '3': 'Mar',
      '4': 'Apr',
      '5': 'May',
      '6': 'Jun',
      '7': 'Jul',
      '8': 'Aug',
      '9': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    },
    days: {
      '0': 'Sunday',
      '1': 'Monday',
      '2': 'Tuesday',
      '3': 'Wednesday',
      '4': 'Thursday',
      '5': 'Friday',
      '6': 'Saturday',
    },
    daysShort: {
      '0': 'Sun',
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
    },
    imageUpload: {
      dragDrop: 'Drag & drop or',
      browse: 'browse',
      sizeHint: 'PNG, JPG up to 5MB',
      replace: 'Replace',
      remove: 'Remove',
      newImageSelected: 'New image selected — save to upload',
      processing: 'Preparing image…',
      notImage: 'Please choose a PNG or JPG image',
      tooLarge: 'Image is too large — please use a file under 5MB',
      processFailed: 'Could not read this image — please try another file',
    },
    operatingHours: {
      addSlot: '+ Add slot',
      noSlots: 'No time slots — click "+ Add slot"',
      closed: 'Closed',
      to: 'to',
      removeSlot: 'Remove slot',
    },
    footer: {
      copyright: '© {year} BatamX Admin',
      tagline: 'Built for multi-platform admin (Booking, E-Commerce, …)',
      platformCopyright: '© {year} BatamX Platform. All rights reserved.',
    },
    terms: {
      agreePrefix: 'By continuing, you agree to the',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      terms: 'Terms',
      privacy: 'Privacy',
      and: 'and',
    },
    aria: {
      openSidebar: 'Open sidebar',
      closeSidebar: 'Close sidebar',
      sidebarNavigation: 'Sidebar navigation',
      closeModal: 'Close',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      removeSlot: 'Remove slot',
      removeChoice: 'Remove choice',
    },
  },

  nav: {
    brand: {
      name: 'BatamX Admin',
      subtitle: 'Platform Console',
    },
    sections: {
      overview: 'Overview',
      booking: 'Booking',
      maintenance: 'Maintenance',
      configuration: 'Configuration',
      platform: 'Platform',
    },
    items: {
      dashboard: 'Dashboard',
      bookings: 'Bookings',
      services: 'Services',
      serviceCategories: 'Service Categories',
      customForms: 'Custom Forms',
      users: 'Users',
      customers: 'Customers',
      setting: 'Setting',
      appearance: 'Appearance',
      aboutUs: 'About Us',
      whatsapp: 'WhatsApp',
      projects: 'Projects',
    },
  },

  auth: {
    login: {
      title: 'Welcome back',
      subtitle: 'Sign in to your admin account to continue.',
      usernameLabel: 'Username',
      usernamePlaceholder: 'Enter your username',
      passwordLabel: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      submit: 'Sign in',
      submitting: 'Signing in…',
      errorFallback: 'Login failed',
      heroTitleLine1: 'Manage your',
      heroTitleLine2: 'business with',
      heroTitleHighlight: 'confidence.',
      heroSubtitle: 'Bookings, services, customers, and analytics — all in one powerful admin dashboard.',
      featureAnalytics: 'Real-time dashboard & analytics',
      featureBooking: 'Seamless booking management',
      featureStaff: 'Customer & staff management',
    },
    register: {
      title: 'Create your account',
      subtitle: 'Fill in the details below to get started.',
      fullNameLabel: 'Full name',
      fullNamePlaceholder: 'Jane Doe',
      emailLabel: 'Email',
      emailPlaceholder: 'you@company.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Create a strong password',
      passwordHint: 'Must be at least 6 characters.',
      submit: 'Create account',
      submitting: 'Creating account…',
      errorFallback: 'Registration failed',
      alreadyHaveAccount: 'Already have an account?',
      signInInstead: 'Sign in instead',
      heroTitleLine1: 'Start managing',
      heroTitleLine2: 'your business',
      heroTitleHighlight: 'today.',
      heroSubtitle: 'Create your admin account and get access to all platform features instantly.',
      featureSetup: 'Set up in under 2 minutes',
      featureSecure: 'Secure & encrypted by default',
      featureAccess: 'Full access to all features',
    },
    resetPassword: {
      checking: 'Checking link…',
      title: 'Set a new password',
      subtitle: 'Your new password should be different from any you have used before.',
      passwordLabel: 'New password',
      passwordPlaceholder: 'Enter a new password',
      passwordHint: 'Must be at least 6 characters.',
      confirmLabel: 'Confirm password',
      confirmPlaceholder: 'Repeat the new password',
      showPassword: 'Show password',
      submit: 'Reset password',
      submitting: 'Saving…',
      rememberedPrompt: 'Already know your password?',
      backToLogin: 'Back to login',
      invalidTitle: 'Link invalid or expired',
      invalidBody: 'Ask an administrator to generate a new reset link for you.',
      successTitle: 'Password updated',
      successBody: 'You can now sign in with your new password. Redirecting to login…',
      goToLogin: 'Go to login',
      errors: {
        passwordMinLength: 'Password must be at least 6 characters.',
        mismatch: 'The passwords do not match.',
        failed: 'Password reset failed. Please try again.',
      },
    },
    brand: {
      name: 'BatamX',
    },
  },

  dashboard: {
    welcomeBack: 'Welcome back',
    welcomeBackWithName: 'Welcome back, {name}',
    overviewFor: 'Business overview for {month} {year}.',
    downloadPdf: 'Download PDF',
    generatingPdf: 'Generating…',
    loadFailed: 'Failed to load dashboard',
    stats: {
      revenue: 'Revenue',
      bookings: 'Bookings',
      netProfit: 'Net Profit',
      expenses: 'Expenses',
      avgBookingValue: 'Avg Booking Value',
      commission: 'Commission',
      customers: 'Customers',
      staff: 'Staff',
      vsLastMonth: 'vs last month',
      completed: 'completed',
      marginSuffix: '{value}% margin',
      itemsSuffix: '{count} items',
      entriesSuffix: '{count} entries',
      servicesSuffix: '{count} services',
    },
    charts: {
      topServices: 'Top Services',
      noDataThisMonth: 'No data this month',
      revenueByPayment: 'Revenue by Payment',
      staffPerformance: 'Staff Performance',
      paymentBreakdown: 'Payment Breakdown',
      revenue: 'Revenue',
      expensesLabel: 'Expenses',
      paid: 'Paid',
      unpaid: 'Unpaid',
      noExpenses: 'No expenses',
      bookingsCountAndAmount: '{count} bookings · {amount}',
    },
    recentBookings: {
      title: 'Recent Bookings',
      empty: 'No recent bookings',
      customer: 'Customer',
      service: 'Service',
      reservationDate: 'Reservation Date',
      amount: 'Amount',
      status: 'Status',
      walkIn: 'Walk-in',
    },
    pdf: {
      title: 'BatamX Monthly Report',
      topServicesNoData: 'Top Services — no data',
      revenueByPaymentNoData: 'Revenue by Payment — no data',
      staffPerformanceNoData: 'Staff Performance — no data',
      paymentBreakdownNoData: 'Payment Breakdown — no data',
      generatedOn: 'Generated on {datetime}',
    },
  },

  booking: {
    title: 'Bookings',
    subtitle: 'View and manage service bookings.',
    loadFailed: 'Failed to load bookings',
    searchPlaceholder: 'Service or customer name…',
    loadingBookings: 'Loading bookings…',
    emptyState: 'No bookings found.',
    updateFailed: 'Failed to update booking',
    statusUpdateFailed: 'Status update failed',
    minutesSuffix: '{value} min',
    table: {
      bookingId: 'Booking ID',
      services: 'Service(s)',
      customer: 'Customer',
      reservation: 'Reservation',
      duration: 'Duration',
      totalPrice: 'Total Price',
      status: 'Status',
      payment: 'Payment',
      actions: 'Actions',
    },
    detail: {
      viewTitle: 'Booking Details',
      editTitle: 'Edit Booking',
      bookingId: 'Booking ID',
      customer: 'Customer',
      customerContact: 'Customer Contact',
      attendedBy: 'Attended By',
      reservation: 'Reservation',
      endTime: 'End Time',
      duration: 'Duration',
      address: 'Address',
      external: 'External',
      status: 'Status',
      payment: 'Payment',
      paymentMethod: 'Payment Method',
      remark: 'Remark',
      created: 'Created',
      services: 'Services',
      service: 'Service',
      price: 'Price',
      totalPrice: 'Total Price',
      total: 'Total',
      yes: 'Yes',
    },
    actions: {
      view: 'View',
      edit: 'Edit',
      cancelBooking: 'Cancel Booking',
      confirm: 'Confirm',
      complete: 'Complete',
      updating: 'Updating…',
    },
    edit: {
      reservationDateTime: 'Reservation Date & Time',
      status: 'Status',
      paymentStatus: 'Payment Status',
      paymentMethod: 'Payment Method',
      attendedAddress: 'Attended Address',
      attendedAddressPlaceholder: 'Optional address…',
      externalBooking: 'External Booking',
      remark: 'Remark',
      remarkPlaceholder: 'Optional remark…',
      servicesSection: 'Services',
      addService: '+ Add Service',
      noServicesAdded: 'No services added. Click "Add Service" to begin.',
      serviceNumber: 'Service #{n}',
      removeService: 'Remove',
      service: 'Service',
      selectService: 'Select service…',
      deleted: 'Deleted',
      price: 'Price',
      attendedByField: 'Attended By',
      selectStaff: 'Select staff…',
      addStaff: '+ Add staff',
      durationCatalog: 'Duration: {duration} min · Catalog price: {price}',
      estimatedTotal: 'Estimated total: {amount}',
      customerReadOnly: 'Customer:',
      save: 'Save Changes',
      saving: 'Saving…',
    },
  },

  services: {
    title: 'Services',
    subtitle: 'Manage bookable services and pricing.',
    loadFailed: 'Failed to load services',
    saveFailed: 'Save failed',
    duplicateFailed: 'Duplicate failed',
    deleteFailed: 'Delete failed',
    confirmDuplicate: 'Duplicate "{title}"? A copy will be created as inactive.',
    confirmDelete: 'Delete "{title}"? This cannot be undone.',
    addService: 'Add service',
    editService: 'Edit service',
    reorder: 'Reorder',
    reorderTitle: 'Reorder Services',
    reorderSubtitle: 'Drag and drop to set the display order.',
    saveOrder: 'Save Order',
    savedOrderFor: 'Saved order for {count} service(s).',
    saveOrderFailed: 'Failed to save order',
    unsavedChanges: 'You have unsaved changes. Click "Save Order" to persist.',
    noServicesInCategory: 'No services in this category.',
    category: 'Category',
    allCategories: 'All categories',
    uncategorized: 'Uncategorized',
    searchPlaceholder: 'Search by title or code…',
    loadingServices: 'Loading services…',
    minutesSuffix: '{value} min',
    table: {
      position: 'Pos',
      title: 'Title',
      code: 'Code',
      type: 'Type',
      price: 'Price',
      duration: 'Duration',
      status: 'Status',
      actions: 'Actions',
    },
    sections: {
      basicInfo: 'Basic Info',
      pricing: 'Pricing',
      description: 'Description',
      staffAvailability: 'Staff & Availability',
      validityPeriod: 'Validity Period',
      subscription: 'Subscription',
    },
    fields: {
      image: 'Image',
      title: 'Title',
      titlePlaceholder: 'e.g. Full Body Massage',
      code: 'Code',
      codePlaceholder: 'e.g. SVC-001',
      serviceType: 'Service Type',
      noneOption: '— None —',
      status: 'Status',
      position: 'Position',
      positionPlaceholder: 'Display order (e.g. 1, 2, 3)',
      positionHint: 'Lower numbers appear first. Use Reorder mode for drag-and-drop.',
      price: 'Price',
      currency: 'Currency',
      commissionPercentage: 'Commission %',
      commissionPlaceholder: 'e.g. 10',
      durationMinutes: 'Duration (minutes)',
      durationPlaceholder: 'e.g. 60',
      descriptionPlaceholder: 'Service description shown to customers…',
      remark: 'Remark',
      remarkPlaceholder: 'Internal notes (not shown to customers)',
      allowedStaff: 'Allowed staff',
      noStaffSelected: 'No staff selected — all staff can attend',
      allowExternalBooking: 'Allow external booking',
      allowExternalBookingHint: 'Customers can book this service from the portal',
      validFrom: 'Valid from',
      validTo: 'Valid to',
      subscriptionService: 'Subscription service',
      subscriptionServiceHint: 'Customers pay once and can book multiple times',
      usageLimit: 'Usage limit',
      usageLimitPlaceholder: 'e.g. 10',
      expiryDays: 'Expiry (days)',
      expiryDaysPlaceholder: 'e.g. 30',
    },
    validation: {
      titleRequired: 'Title is required.',
      codeRequired: 'Code is required.',
      priceInvalid: 'Price must be a valid non-negative number.',
    },
  },

  categories: {
    title: 'Service Categories',
    subtitle: 'Manage service types and their display order.',
    loadFailed: 'Failed to load categories',
    addCategory: 'Add category',
    editCategory: 'Edit category',
    categoryCreated: 'Category created.',
    categoryUpdated: 'Category updated.',
    categoryDeleted: 'Category deleted.',
    confirmDelete: 'Delete "{name}"? This cannot be undone.',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    searchPlaceholder: 'Search by name…',
    loadingCategories: 'Loading categories…',
    table: {
      name: 'Name',
      position: 'Position',
      services: 'Services',
      actions: 'Actions',
    },
    fields: {
      image: 'Image',
      name: 'Name',
      namePlaceholder: 'e.g. Massage, Cleaning',
      position: 'Position',
      positionPlaceholder: 'Display order (e.g. 1, 2, 3)',
      positionHint: 'Lower numbers appear first.',
    },
    validation: {
      nameRequired: 'Name is required.',
      positionInvalid: 'Position must be a non-negative number.',
    },
  },

  customForms: {
    title: 'Custom Forms',
    subtitle: 'Manage custom form fields for service bookings.',
    loadFailed: 'Failed to load custom forms',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    addField: 'Add field',
    addFormField: 'Add custom form field',
    editFormField: 'Edit custom form field',
    confirmDelete: 'Delete custom form "{label}"? This cannot be undone.',
    searchPlaceholder: 'Search by key or label…',
    loadingForms: 'Loading custom forms…',
    table: {
      key: 'Key',
      label: 'Label',
      type: 'Type',
      required: 'Required',
      sequence: 'Sequence',
      actions: 'Actions',
    },
    fields: {
      key: 'Key',
      keyPlaceholder: 'e.g. shirt_size',
      label: 'Label',
      labelPlaceholder: 'e.g. Shirt Size',
      sequence: 'Sequence',
      type: 'Type',
      choices: 'Choices',
      choicePlaceholder: 'Choice {n}',
      addChoice: '+ Add choice',
      required: 'Required',
      applyToAllServices: 'Apply to all services',
      services: 'Services',
      selectServicesPlaceholder: 'Select services…',
      noServicesAvailable: 'No services available',
      instruction: 'Instruction',
      instructionPlaceholder: 'Optional help text shown to the customer…',
    },
    typeOptions: {
      input: 'Input',
      choices: 'Choices',
      number: 'Number',
      date: 'Date',
      dob: 'Date of Birth',
    },
    validation: {
      keyRequired: 'Key is required.',
      labelRequired: 'Label is required.',
      choicesRequired: 'At least one choice is required for "choices" type.',
    },
  },

  users: {
    title: 'Users',
    subtitle: 'Manage admin and staff users.',
    loadFailed: 'Failed to load users',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    addUser: 'Add user',
    editUser: 'Edit user',
    confirmDelete: 'Delete "{name}"? This cannot be undone.',
    searchPlaceholder: 'Search by name, email, role, status…',
    loadingUsers: 'Loading users…',
    table: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      status: 'Status',
      actions: 'Actions',
    },
    sections: {
      profilePicture: 'Profile Picture',
      account: 'Account',
      contact: 'Contact',
      professional: 'Professional',
    },
    fields: {
      photo: 'Photo',
      profileDisplay: 'Profile Display',
      performService: 'Perform Service',
      name: 'Name',
      namePlaceholder: 'Jane Doe',
      title: 'Title',
      titlePlaceholder: 'e.g. Manager, Therapist',
      username: 'Username',
      usernamePlaceholder: 'Defaults to email',
      email: 'Email',
      emailPlaceholder: 'jane@company.com',
      password: 'Password',
      passwordPlaceholder: 'Min 6 characters',
      role: 'Role',
      status: 'Status',
      phone: 'Phone',
      phonePlaceholder: '+628xxx',
      country: 'Country',
      countryPlaceholder: 'ID',
      address: 'Address',
      addressPlaceholder: 'Street address',
      service: 'Consultation Service',
      servicePlaceholder: 'e.g. Nutrition consultation, Physiotherapy',
      occupation: 'Occupation',
      occupationPlaceholder: 'e.g. Therapist',
      description: 'Description',
      descriptionPlaceholder: 'Brief bio or description',
    },
    validation: {
      nameRequired: 'Name is required.',
      emailInvalid: 'A valid email is required.',
      passwordMinLength: 'Password must be at least 6 characters.',
    },
    resetPassword: {
      action: 'Reset link',
      generating: 'Generating…',
      title: 'Password reset link',
      description: 'Share this link with {name} so they can set a new password.',
      copy: 'Copy',
      copied: 'Link copied to clipboard.',
      copyHint: 'Select the link above to copy it manually.',
      expiryNote: 'The link expires in 24 hours and replaces any link issued earlier.',
      failed: 'Could not generate the reset link.',
    },
  },

  customers: {
    title: 'Customers',
    subtitle: 'Manage customer accounts.',
    loadFailed: 'Failed to load customers',
    saveFailed: 'Save failed',
    addCustomer: 'Add customer',
    editCustomer: 'Edit customer',
    searchPlaceholder: 'Search by name, email, phone…',
    loadingCustomers: 'Loading customers…',
    table: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      dob: 'DOB',
      status: 'Status',
      actions: 'Actions',
      createdOn: 'Created {date}',
    },
    sections: {
      account: 'Account',
      personalInfo: 'Personal Info',
    },
    fields: {
      name: 'Name',
      namePlaceholder: 'Jane Doe',
      email: 'Email',
      emailPlaceholder: 'jane@example.com',
      username: 'Username',
      usernamePlaceholder: 'jane.doe (defaults to email)',
      password: 'Password',
      passwordPlaceholder: 'Min 6 characters',
      dob: 'Date of Birth',
      phone: 'Phone',
      phonePlaceholder: '+628xxx',
      country: 'Country',
      countryPlaceholder: 'ID',
      type: 'Type',
      typePlaceholder: 'e.g. VIP, Regular',
      address: 'Address',
      addressPlaceholder: 'Street address',
    },
    validation: {
      nameRequired: 'Name is required.',
      emailInvalid: 'A valid email is required.',
      passwordMinLength: 'Password must be at least 6 characters.',
    },
  },

  projects: {
    title: 'Projects',
    subtitle: 'Manage all tenant projects on the platform.',
    loadFailed: 'Failed to load projects',
    saveFailed: 'Save failed',
    switchFailed: 'Failed to switch project',
    addProject: 'Add project',
    editProject: 'Edit project',
    confirmSwitch: 'Switch to project "{name}"? The page will reload.',
    searchPlaceholder: 'Search by name, code, sector…',
    loadingProjects: 'Loading projects…',
    table: {
      name: 'Name',
      code: 'Code',
      sector: 'Sector',
      contact: 'Contact',
      country: 'Country',
      test: 'Test',
      status: 'Status',
      actions: 'Actions',
    },
    badges: {
      test: 'Test',
      live: 'Live',
      active: 'Active',
      disabled: 'Disabled',
    },
    toast: {
      enabled: '"{name}" is enabled — its booking site is live again.',
      disabled: '"{name}" is disabled — its booking site is now offline.',
    },
    rowActions: {
      roles: 'Roles',
      edit: 'Edit',
      access: 'Access',
    },
    fields: {
      name: 'Name',
      namePlaceholder: 'e.g. Batam Spa & Wellness',
      code: 'Code',
      codePlaceholder: 'e.g. BSW',
      description: 'Description',
      descriptionPlaceholder: 'Short description (optional)',
      sector: 'Sector',
      sectorPlaceholder: 'e.g. Wellness, F&B, Retail',
      address: 'Address',
      addressPlaceholder: 'Business address (optional)',
      email: 'Email',
      emailPlaceholder: 'Contact email (optional)',
      contactPerson: 'Contact Person',
      contactPersonPlaceholder: 'Contact person name',
      contactNo: 'Contact Number',
      contactNoPlaceholder: 'e.g. +62 812 3456 7890',
      country: 'Country',
      currency: 'Currency',
      testProject: 'Test project',
      activeProject: 'Project enabled',
      activeProjectHint: 'When disabled, the booking site stops resolving this project and shows Not Found. Admin access is unaffected.',
    },
    countries: {
      id: 'Indonesia',
      sg: 'Singapore',
      my: 'Malaysia',
    },
    validation: {
      nameRequired: 'Name is required.',
      codeRequired: 'Code is required.',
      sectorRequired: 'Sector is required.',
      contactPersonRequired: 'Contact person is required.',
      contactNoRequired: 'Contact number is required.',
    },
    seedRoles: {
      title: 'Access roles',
      subtitle: 'Each role is created with its own login. Passwords are set on first sign-in.',
      bookingHint:
        'Booking is enabled: 2 placeholder categories with 1 service each will be created, along with onsite & offsite hours open every day (09:00–18:00).',
    },
    roles: {
      title: 'Roles — {project}',
      addRole: 'Add role',
      editRole: 'Edit role',
      loadingRoles: 'Loading roles…',
      rolesConfigured: '{count} role(s) configured',
      noRolesConfigured: 'No roles configured yet. Create one to get started.',
      roleName: 'Role Name',
      roleNamePlaceholder: 'e.g. admin, staff, manager',
      modulePermissions: 'Module Permissions',
      roleCreated: 'Role created.',
      roleUpdated: 'Role updated.',
      roleDeleted: 'Role deleted.',
      confirmDelete: 'Delete role "{role}"? This cannot be undone.',
      loadFailed: 'Failed to load roles',
      saveFailed: 'Save failed',
      deleteFailed: 'Delete failed',
      roleNameRequired: 'Role name is required.',
      enabled: 'Enabled',
      disabled: 'Disabled',
      modules: {
        dashboard: 'Dashboard',
        booking: 'Booking',
        ecommerce: 'Ecommerce',
        user: 'Users',
        customer: 'Customers',
        notification: 'Notification',
        custom: 'Custom',
        reward: 'Reward',
        rental: 'Rental',
        project: 'Project',
      },
    },
  },

  settings: {
    title: 'Project Settings',
    subtitle: 'Manage general configuration, booking, and notification preferences.',
    loadFailed: 'Failed to load settings',
    saveFailed: 'Failed to save settings',
    saveSuccess: 'Settings saved successfully',
    reloadFailed: 'Failed to reload',
    sections: {
      general: {
        title: 'General',
        subtitle: 'Core access and messaging settings',
      },
      contactInformation: {
        title: 'Contact Information',
        subtitle: 'Address and contact details displayed to customers',
      },
      bookingConfiguration: {
        title: 'Booking Configuration',
        subtitle: 'Control booking types, labels, and operation intervals',
      },
      operatingHours: {
        title: 'Operating Hours',
        subtitle: 'Weekly schedules for offsite and onsite bookings',
      },
      additionalSettings: {
        title: 'Additional Settings',
        subtitle: 'Commission rates and notification templates',
      },
    },
    dividers: {
      information: 'Information',
      booking: 'Booking',
      operatingHours: 'Operating Hours',
      others: 'Others',
    },
    fields: {
      allowCustomerLogin: 'Allow Customer Login',
      allowCustomerLoginHint: 'Enable customers to log in to the public-facing app',
      productSectionEnabled: 'Show Product Section',
      productSectionEnabledHint: 'Display the product catalogue on the public-facing home page',
      motto: 'Motto',
      mottoPlaceholder: 'Your project motto',
      welcomeMessage: 'Welcome Message',
      welcomeMessagePlaceholder: 'Welcome message for customers',
      address: 'Address',
      addressPlaceholder: 'Full business address',
      addressLink: 'Address Link',
      addressLinkPlaceholder: 'Google Maps URL or similar',
      contactNo: 'Contact Number',
      contactNoPlaceholder: '+65 1234 5678',
      contactEmail: 'Contact Email',
      contactEmailPlaceholder: 'contact@example.com',
      allowOffsiteBooking: 'Allow Offsite Booking',
      allowOffsiteBookingHint: 'Customers can book services offsite',
      allowOnsiteBooking: 'Allow Onsite Booking',
      allowOnsiteBookingHint: 'Customers can book services at your location',
      offsiteLabel: 'Offsite Label',
      offsiteLabelPlaceholder: 'e.g. Delivery, Home Service',
      onsiteLabel: 'Onsite Label',
      onsiteLabelPlaceholder: 'e.g. Walk-in, In-Store',
      defaultLanguage: 'Default Language',
      defaultLanguageHint: 'Language the booking site opens in for visitors who have not picked one themselves.',
      defaultLanguageAuto: 'Follow visitor / project country',
      defaultLanguageEn: 'English',
      defaultLanguageId: 'Bahasa Indonesia',
      remarksLabel: 'Remarks Label',
      remarksLabelPlaceholder: 'e.g. Remarks, Notes, Special requests',
      offsiteInterval: 'Offsite Operation Interval',
      offsiteIntervalPlaceholder: 'e.g. 30 (minutes)',
      onsiteInterval: 'Onsite Operation Interval',
      onsiteIntervalPlaceholder: 'e.g. 30 (minutes)',
      commissionPercentage: 'Booking Commission (%)',
      commissionPercentagePlaceholder: 'e.g. 10',
      invoiceNotes: 'Invoice Notes',
      invoiceNotesPlaceholder: 'Notes to display on invoices',
    },

    whatsapp: {
      title: 'WhatsApp Notifications',
      subtitle: 'Wording and on/off switches for every message your staff can send.',
      manualHint: 'Messages are never sent automatically. Turning one on adds a button in the app that opens WhatsApp with the message ready — a staff member still presses send. Changes appear in the app the next time staff open the relevant page.',
      loadFailed: 'Could not load notifications',
      saveSuccess: 'Notification saved',
      saveFailed: 'Could not save notification',
      resetSuccess: 'Restored the default wording',
      resetFailed: 'Could not restore the default',
      resetToDefault: 'Reset to default',
      customised: 'Edited',
      message: 'Message',
      variables: 'Insert a detail',
      showPreview: 'Show preview',
      hidePreview: 'Hide preview',
      previewEmpty: 'Nothing to preview yet.',
      tooLong: 'Trim to {max} characters or fewer to save.',
      on: 'On',
      off: 'Off',
      groupCount: '{enabled} of {total} on',
      groups: {
        booking: 'Bookings',
        rental: 'Rental',
        customer: 'Customers',
      },
      templates: {
        'booking.confirmed': 'Booking confirmed',
        'booking.reminder': 'Booking reminder',
        'booking.invoice': 'Booking invoice',
        'booking.completed': 'Booking completed',
        'booking.rescheduled': 'Booking rescheduled',
        'booking.cancelled': 'Booking cancelled',
        'booking.staffAssignment': 'Staff assignment',
        'rental.paymentDue': 'Rent due',
        'rental.paymentOverdue': 'Rent overdue',
        'rental.leaseExpiring': 'Lease expiring',
        'rental.invoice': 'Rental invoice',
        'rental.leaseConfirmed': 'Lease started',
        'rental.paymentReceived': 'Payment received',
        'rental.returnSummary': 'Return summary',
        'rental.depositReceived': 'Deposit received',
        'customer.bookingLink': 'Personal booking link',
        'customer.welcome': 'Welcome message',
      },
      descriptions: {
        'booking.confirmed': 'Sent when a pending booking is confirmed and staff are assigned.',
        'booking.reminder': 'Reminder for an upcoming booking, usually sent the day before.',
        'booking.invoice': 'Sends the invoice link for a booking.',
        'booking.completed': 'Thank-you note after a booking is marked completed.',
        'booking.rescheduled': 'Sent when the date or time of an existing booking changes.',
        'booking.cancelled': 'Sent when a booking is cancelled.',
        'booking.staffAssignment': 'Sent to the assigned staff member, not the customer.',
        'rental.paymentDue': 'Reminder for a rental payment that is upcoming or pending.',
        'rental.paymentOverdue': 'Chaser for a rental payment that is past its due date.',
        'rental.leaseExpiring': 'Renewal prompt for a lease approaching its end date.',
        'rental.invoice': 'Sends the invoice link for a lease.',
        'rental.leaseConfirmed': 'Sent when a lease becomes active, usually right after checkout.',
        'rental.paymentReceived': 'Receipt sent when a rental payment is marked paid.',
        'rental.returnSummary': 'Closing summary when an asset is returned, including any refund.',
        'rental.depositReceived': 'Receipt sent when a lease deposit is marked paid.',
        'customer.bookingLink': "Shares the customer's personal booking link alongside their QR code.",
        'customer.welcome': 'Greeting for a newly registered customer.',
      },
    },
    scheduleTypes: {
      offsite: 'Offsite Schedule',
      onsite: 'Onsite Schedule',
    },
  },

  appearance: {
    title: 'Appearance',
    subtitle: 'Manage your project branding, images, and color scheme.',
    sections: {
      brandImages: {
        title: 'Brand Images',
        subtitle: 'Cover image and logo displayed in the customer-facing app',
      },
      brandColors: {
        title: 'Brand Colors',
        subtitle: 'Color scheme used in the customer-facing application',
      },
    },
    dividers: {
      colors: 'Colors',
    },
    fields: {
      coverImage: 'Cover Image',
      logo: 'Logo',
      themeColor: 'Theme Color',
      buttonColor: 'Button Color',
      serviceColor: 'Service Color',
    },
  },

  aboutUs: {
    title: 'About Us',
    subtitle: 'Manage the About Us content displayed to customers.',
    enableToggle: 'Enable About Us',
    enableToggleHint: 'Show the About Us section in the customer-facing app',
    sectionNumber: 'Section {n}',
    removeSection: 'Remove',
    addSection: '+ Add New Section',
    sectionImage: 'Section Image',
    sectionTitle: 'Title',
    sectionTitlePlaceholder: 'Section title',
    sectionContentLabel: 'Content',
    sectionContentPlaceholder: 'Write the section content…',
    loadFailed: 'Failed to load About Us settings',
    saveFailed: 'Failed to save About Us',
    saveSuccess: 'About Us saved successfully',
  },
} satisfies MessageTree;

export default en;
